import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import { CreditCurrencyConversionRates, ExportJobUpdate } from "src/graphql/generated/graphql";
import { downloadExportJobMutation } from "src/graphql/operations/system/mutations";
import { systemCreditCurrencyConversionRatesQuery } from "src/graphql/operations/system/query";
import { onExportJobUpdateSubscription } from "src/graphql/operations/system/subscriptions";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { graphqlSubscribe } from "src/graphql/utils/graphqlSubscribe";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";

export const useSystemCreditCurrencyConversionRates = () => {
    const { client } = useAuthContext();

    const [creditCurrencyConversionRates, setCreditCurrencyConversionRates] = useState<CreditCurrencyConversionRates>();
    const [isLoadingCreditCurrencyConversionRates, setIsLoadingCreditCurrencyConversionRates] = useState(false);

    const fetchCreditCurrencyConversionRates = useCallback(async (clientId: string) => {
        setIsLoadingCreditCurrencyConversionRates(true);

        try {
            const response = await graphqlFetch(systemCreditCurrencyConversionRatesQuery, { clientId });

            setCreditCurrencyConversionRates(response.system?.creditCurrencyConversionRates);
        } catch (err) {
            handleError(err, { silent: true, context: { clientId } });
        } finally {
            setIsLoadingCreditCurrencyConversionRates(false);
        }
    }, []);

    useEffect(() => {
        if (!client?.id) {
            return;
        }

        fetchCreditCurrencyConversionRates(client?.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.id]);

    return {
        creditCurrencyConversionRates,
        isLoadingCreditCurrencyConversionRates,
        reloadCreditCurrencyConversionRates: useCallback(() => {
            if (!client?.id) {
                return;
            }

            return fetchCreditCurrencyConversionRates(client?.id);
        }, [fetchCreditCurrencyConversionRates, client?.id]),
    };
};

export const useDownloadExportJob = () => {
    const { t } = useTranslation(["common"]);

    const [isDownloadingExportJob, setIsDownloadingExportJob] = useState(false);

    const downloadExportJob = useCallback(
        async (jobId: string) => {
            setIsDownloadingExportJob(true);
            showNotification({
                color: "blue",
                message: t("exportjob_downloading"),
                loading: true,
            });

            try {
                const response = await graphqlFetch(downloadExportJobMutation, { jobId });

                cleanNotifications();
                showNotification({
                    color: "green",
                    message: t("exportjob_downloading_success"),
                });

                return response.downloadExportJob;
            } catch (err) {
                handleError(err, {
                    message: { key: "exportjob_downloading_failed", namespace: "common" },
                    context: { jobId },
                });
            } finally {
                setIsDownloadingExportJob(false);
            }
        },
        [t]
    );

    return {
        isDownloadingExportJob,
        downloadExportJob,
    };
};

export const useSubscribeToExportJobUpdates = () => {
    const subscribeToExportJobUpdates = useCallback(
        async (jobId: string, onUpdate: (value: ExportJobUpdate) => unknown) => {
            let subscription: import("zen-observable-ts").ZenObservable.Subscription;

            try {
                subscription = graphqlSubscribe(onExportJobUpdateSubscription, { jobId }).subscribe({
                    next({ value }) {
                        onUpdate(value?.data?.onExportJobUpdate);
                    },
                    error(err) {
                        handleError(err, { silent: true, context: { jobId } });
                    },
                });
            } catch (err) {
                handleError(err, { silent: true, context: { jobId } });
            }

            return () => {
                subscription?.unsubscribe?.();
                subscription = null;
            };
        },
        []
    );

    return { subscribeToExportJobUpdates };
};
